import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, Check, KeyRound, QrCode, RotateCcw, Search, Utensils, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { isTableServiceEnabled } from '@/lib/storefrontFulfillment';
import { resolvePageRestaurantPalette } from '@/lib/restaurantPalettes';
import { useStoreEntry } from './useStoreEntry';

type TableContextVariant = 'hero' | 'chip' | 'card';

const tableLabel = (table: { name: string; zone: string | null } | null) =>
  table ? [table.zone, table.name].filter(Boolean).join(' · ') : 'Table non sélectionnée';

export function StoreTableContext({
  variant = 'card',
  customerName: _customerName,
  purpose = 'service',
}: {
  variant?: TableContextVariant;
  customerName?: string | null;
  purpose?: 'service' | 'pickup';
}) {
  const {
    page,
    originTable,
    serviceTable,
    diningTables,
    hasQrTableOrigin,
    tablePresenceLevel,
    tableClaim,
    setOriginTableId,
    setServiceTableId,
  } = useStoreEntry();
  const tableServiceEnabled = isTableServiceEnabled(page);

  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState<'origin' | 'service'>(
    originTable ? 'service' : 'origin',
  );
  const [tableSearchInput, setTableSearchInput] = useState('');
  const [selectedZone, setSelectedZone] = useState<string>('all');

  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const sameTable = originTable?.id === serviceTable?.id;

  const safeDiningTables = diningTables ?? [];

  const zones = useMemo(() => {
    const set = new Set<string>();
    safeDiningTables.forEach((t) => {
      if (t.zone) set.add(t.zone);
    });
    return Array.from(set);
  }, [safeDiningTables]);

  const filteredTables = useMemo(() => {
    const q = tableSearchInput.trim().toLowerCase();
    return safeDiningTables.filter((t) => {
      const matchZone = selectedZone === 'all' || t.zone === selectedZone;
      if (!matchZone) return false;
      if (!q) return true;
      return t.name.toLowerCase().includes(q) || (t.zone ?? '').toLowerCase().includes(q);
    });
  }, [safeDiningTables, selectedZone, tableSearchInput]);

  const closeSelector = () => {
    setSelectorOpen(false);
    setTableSearchInput('');
    setSelectedZone('all');
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };
  const openSelector = (mode: 'origin' | 'service') => {
    setSelectionMode(mode);
    setSelectorOpen(true);
    window.requestAnimationFrame(() => closeRef.current?.focus());
  };

  const paletteStyle = useMemo(() => {
    return resolvePageRestaurantPalette(page, 'light');
  }, [page]);

  if (!tableServiceEnabled) return null;

  const fullRouteLabel =
    purpose === 'pickup'
      ? tableLabel(originTable)
      : sameTable
        ? tableLabel(serviceTable)
        : `Origine: ${originTable?.name ?? 'Non définie'} → Service: ${serviceTable?.name ?? 'Non définie'}`;

  if (purpose === 'service' && tablePresenceLevel === 'strict' && !tableClaim) {
    return null;
  }

  return (
    <>
      {variant === 'chip' ? (
        <button
          ref={triggerRef}
          type="button"
          onClick={() =>
            openSelector(purpose === 'pickup' ? 'origin' : originTable ? 'service' : 'origin')
          }
          className={`inline-flex min-h-9 cursor-pointer items-center gap-2 rounded-full border px-3 text-xs font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--store-accent)] ${
            tableServiceEnabled
              ? 'border-[var(--store-border)] bg-[var(--store-surface-low)] text-[var(--store-ink)] hover:border-[var(--store-accent)] shadow-2xs'
              : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 dark:border-charcoal-700 dark:bg-charcoal-900 dark:text-slate-200'
          }`}
        >
          <Utensils
            className={`size-3.5 ${tableServiceEnabled ? 'text-[var(--store-accent)]' : 'text-slate-400'}`}
            aria-hidden
          />
          <span className="truncate max-w-[160px]">
            {originTable ? originTable.name : 'Choisir ma table'}
          </span>
        </button>
      ) : (
        <div
          className={`store-flow-card flex flex-col gap-3 border p-4 sm:flex-row sm:items-center sm:justify-between ${
            variant === 'hero'
              ? 'border-white/20 bg-white/10 text-white backdrop-blur-md'
              : tableServiceEnabled
                ? 'border-[var(--store-border)] bg-[var(--store-surface)] text-[var(--store-ink)] shadow-xs'
                : 'border-slate-200 bg-white dark:border-charcoal-800 dark:bg-charcoal-900'
          }`}
        >
          <div className="flex min-w-0 items-center gap-3">
            <span
              className={`grid size-10 shrink-0 place-items-center rounded-xl ${
                variant === 'hero'
                  ? 'bg-white/20 text-white'
                  : tableServiceEnabled
                    ? 'bg-[var(--store-surface-low)] text-[var(--store-accent)]'
                    : 'bg-slate-100 text-slate-700 dark:bg-charcoal-800 dark:text-slate-200'
              }`}
            >
              <Utensils className="size-5" />
            </span>
            <div className="min-w-0">
              <p
                className={`font-mono text-[10px] font-bold uppercase tracking-[0.14em] ${
                  variant === 'hero'
                    ? 'text-white/70'
                    : tableServiceEnabled
                      ? 'text-[var(--store-ink-muted)]'
                      : 'text-slate-500'
                }`}
              >
                {purpose === 'pickup'
                  ? originTable
                    ? 'Retrait rattaché à votre table'
                    : 'Table de rattachement'
                  : originTable
                    ? 'Service à table actif'
                    : 'Service à table'}
              </p>
              <p
                className={`mt-0.5 text-sm font-bold ${
                  variant === 'hero'
                    ? 'text-white'
                    : tableServiceEnabled
                      ? 'text-[var(--store-ink)]'
                      : 'text-charcoal-950 dark:text-white'
                }`}
              >
                {fullRouteLabel}
              </p>
            </div>
          </div>
          <button
            ref={triggerRef}
            type="button"
            onClick={() =>
              openSelector(purpose === 'pickup' ? 'origin' : originTable ? 'service' : 'origin')
            }
            className={`min-h-11 w-full rounded-xl px-4 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 sm:w-auto sm:justify-self-end ${
              variant === 'hero'
                ? 'bg-white text-charcoal-950 hover:bg-slate-100 focus-visible:ring-white'
                : tableServiceEnabled
                  ? 'bg-[var(--store-accent)] text-[var(--store-accent-foreground)] hover:opacity-90'
                  : 'bg-charcoal-950 text-white hover:bg-charcoal-800 focus-visible:ring-lime-500 dark:bg-white dark:text-charcoal-950'
            }`}
          >
            {originTable
              ? purpose === 'pickup'
                ? 'Changer ma table'
                : 'Changer la table à servir'
              : 'Indiquer ma table'}
          </button>
        </div>
      )}

      {selectorOpen &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="table-selector-title"
            data-storefront-root
            style={paletteStyle as React.CSSProperties}
            className="fixed inset-0 z-[70] grid place-items-end backdrop-blur-md sm:place-items-center sm:p-4 bg-black/60"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeSelector();
            }}
          >
            <div className="max-h-[85dvh] w-full flex flex-col overflow-hidden rounded-t-3xl border border-[var(--store-border)] bg-[var(--store-bg)] text-[var(--store-ink)] shadow-2xl sm:max-w-xl sm:rounded-3xl">
              <div className="flex items-start justify-between border-b border-[var(--store-border)] bg-[var(--store-surface)] p-5">
                <div className="min-w-0 pr-3">
                  <h2
                    id="table-selector-title"
                    className="font-restaurant-display text-lg font-bold text-[var(--store-ink)]"
                  >
                    {purpose === 'pickup'
                      ? 'À quelle table êtes-vous ?'
                      : selectionMode === 'origin'
                        ? 'Où êtes-vous installé ?'
                        : 'Où devons-nous servir ?'}
                  </h2>
                  <p className="mt-1 font-restaurant-body text-xs leading-5 text-[var(--store-ink-muted)]">
                    {purpose === 'pickup'
                      ? 'La commande à emporter et sa facture seront rattachées à cette table.'
                      : selectionMode === 'origin'
                        ? 'Votre emplacement initialise la commande.'
                        : 'Votre table d’origine reste enregistrée pour le personnel.'}
                  </p>
                </div>
                <button
                  ref={closeRef}
                  type="button"
                  aria-label="Fermer"
                  onClick={closeSelector}
                  className="grid size-10 shrink-0 place-items-center rounded-full bg-[var(--store-surface-low)] text-[var(--store-ink)] hover:border border-[var(--store-border)] transition"
                >
                  <X className="size-5" />
                </button>
              </div>

              {originTable && (
                <div className="border-b border-[var(--store-border)] bg-[var(--store-surface-low)] px-5 py-3 text-[var(--store-ink)]">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--store-ink-muted)]">
                        Votre emplacement
                      </p>
                      <p className="mt-1 flex flex-wrap items-center gap-2 text-sm font-bold text-[var(--store-ink)]">
                        {tableLabel(originTable)}
                        {hasQrTableOrigin && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[var(--store-surface)] border border-[var(--store-border)] px-2 py-0.5 text-[10px] text-[var(--store-accent)] shadow-2xs">
                            <QrCode className="size-3" /> Détectée par QR
                          </span>
                        )}
                      </p>
                    </div>
                    {purpose !== 'pickup' && !hasQrTableOrigin && selectionMode === 'service' && (
                      <button
                        type="button"
                        onClick={() => setSelectionMode('origin')}
                        className="text-xs font-bold underline underline-offset-4 text-[var(--store-accent)]"
                      >
                        Changer mon emplacement
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="p-4 pb-2">
                <div className="relative flex items-center">
                  <Search className="absolute left-3.5 size-4 text-[var(--store-accent)]" />
                  <input
                    type="search"
                    value={tableSearchInput}
                    onChange={(e) => setTableSearchInput(e.target.value)}
                    placeholder="Rechercher une table (ex: 12, Terrasse...)"
                    className="h-11 w-full rounded-xl border pl-10 pr-4 text-xs font-medium outline-none transition focus:ring-2 border-[var(--store-border)] bg-[var(--store-surface-low)] text-[var(--store-ink)] placeholder-[var(--store-ink-muted)] focus:border-[var(--store-accent)] focus:ring-[var(--store-accent)]/20"
                  />
                  {tableSearchInput && (
                    <button
                      type="button"
                      onClick={() => setTableSearchInput('')}
                      className="absolute right-3 text-xs opacity-60 hover:opacity-100 text-[var(--store-ink)]"
                    >
                      Effacer
                    </button>
                  )}
                </div>

                {zones.length > 0 && (
                  <div className="mt-3 flex gap-2 overflow-x-auto pb-1 hide-scrollbar">
                    <button
                      type="button"
                      onClick={() => setSelectedZone('all')}
                      className={`rounded-full px-3 py-1 text-xs font-bold whitespace-nowrap transition ${
                        selectedZone === 'all'
                          ? 'bg-[var(--store-accent)] text-[var(--store-accent-foreground)]'
                          : 'bg-[var(--store-surface-low)] border border-[var(--store-border)] text-[var(--store-ink-muted)] hover:text-[var(--store-ink)]'
                      }`}
                    >
                      Toutes ({safeDiningTables.length})
                    </button>
                    {zones.map((z) => {
                      const count = safeDiningTables.filter((t) => t.zone === z).length;
                      return (
                        <button
                          key={z}
                          type="button"
                          onClick={() => setSelectedZone(z)}
                          className={`rounded-full px-3 py-1 text-xs font-bold whitespace-nowrap transition ${
                            selectedZone === z
                              ? 'bg-[var(--store-accent)] text-[var(--store-accent-foreground)]'
                              : 'bg-[var(--store-surface-low)] border border-[var(--store-border)] text-[var(--store-ink-muted)] hover:text-[var(--store-ink)]'
                          }`}
                        >
                          {z} ({count})
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {purpose !== 'pickup' &&
                selectionMode === 'service' &&
                originTable &&
                serviceTable?.id !== originTable.id && (
                  <div className="px-4 pt-1 pb-2">
                    <button
                      type="button"
                      onClick={() => {
                        setServiceTableId(originTable.id);
                        closeSelector();
                      }}
                      className="flex min-h-10 w-full items-center justify-center gap-2 rounded-xl border border-[var(--store-border)] bg-[var(--store-surface-low)] text-[var(--store-accent)] text-xs font-bold transition hover:bg-[var(--store-surface)]"
                    >
                      <RotateCcw className="size-3.5" /> Servir ma table ({originTable.name})
                    </button>
                  </div>
                )}

              <div className="flex-1 min-h-0 overflow-y-auto p-4 pt-1 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
                {filteredTables.length === 0 ? (
                  <div className="py-8 text-center text-xs opacity-60">
                    Aucune table ne correspond à votre recherche.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                    {filteredTables.map((table) => {
                      const selected =
                        purpose === 'pickup' || selectionMode === 'origin'
                          ? originTable?.id === table.id
                          : serviceTable?.id === table.id;
                      return (
                        <button
                          key={table.id}
                          type="button"
                          onClick={() => {
                            if (purpose === 'pickup' || selectionMode === 'origin')
                              setOriginTableId(table.id);
                            else setServiceTableId(table.id);
                            closeSelector();
                          }}
                          className={`flex min-h-[52px] items-center justify-between rounded-xl border px-3.5 py-2.5 text-left transition-all focus-visible:outline-none ${
                            selected
                              ? 'border-2 border-[var(--store-accent)] bg-[var(--store-accent)]/15 text-[var(--store-ink)] font-bold shadow-xs'
                              : 'border border-[var(--store-border)] bg-[var(--store-surface)] text-[var(--store-ink)] hover:border-[var(--store-accent)]/60 shadow-2xs'
                          }`}
                        >
                          <div className="min-w-0 pr-2">
                            <span className="block truncate font-bold text-xs sm:text-sm">
                              {table.name}
                            </span>
                            {table.zone && (
                              <span
                                className={`block truncate text-[10px] ${
                                  selected
                                    ? 'text-[var(--store-accent)]'
                                    : 'text-[var(--store-ink-muted)]'
                                }`}
                              >
                                {table.zone}
                              </span>
                            )}
                          </div>
                          <span className="shrink-0">
                            {selected ? (
                              <Check className="size-4 text-[var(--store-accent)]" />
                            ) : (
                              <ArrowRight className="size-3.5 opacity-30 group-hover:opacity-100" />
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

export function StoreStrictTableClaimDialog() {
  const { originTable, tablePresenceLevel, tableClaim, tableClaimError, submitTableSessionCode } =
    useStoreEntry();
  const [sessionCode, setSessionCode] = useState('');
  const [checkingCode, setCheckingCode] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const errorCode = tableClaimError?.code;
  const needsCode = errorCode === 'SESSION_CODE_REQUIRED' || errorCode === 'INVALID_SESSION_CODE';
  const sessionUnavailable = errorCode === 'TABLE_SESSION_REQUIRED';
  const open =
    tablePresenceLevel === 'strict' &&
    !tableClaim &&
    Boolean(originTable) &&
    (needsCode || sessionUnavailable);

  useEffect(() => {
    if (!open) return;
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  useEffect(() => {
    if (errorCode !== 'INVALID_SESSION_CODE') return;
    setSessionCode('');
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }, [errorCode]);

  if (!open) return null;

  return (
    <Dialog open>
      <DialogContent
        showCloseButton={false}
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        className="bottom-0 left-0 top-auto max-h-[calc(100dvh-1rem)] w-full max-w-none translate-x-0 translate-y-0 gap-0 overflow-hidden rounded-b-none rounded-t-3xl border-0 p-0 sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-w-md sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-3xl"
      >
        <div className="h-1.5 bg-amber-400" />
        <div className="p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:p-7">
          <div className="flex items-start gap-4">
            <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-amber-100 text-amber-800 dark:bg-amber-400/15 dark:text-amber-300">
              <KeyRound className="size-6" aria-hidden />
            </span>
            <div className="min-w-0">
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-amber-700 dark:text-amber-300">
                Commande à table sécurisée
              </p>
              <DialogTitle className="mt-1 text-xl font-black leading-tight text-charcoal-950 dark:text-white">
                Valider {originTable?.name ?? 'votre table'}
              </DialogTitle>
            </div>
          </div>

          <DialogDescription className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
            {sessionUnavailable
              ? 'Cette table doit d’abord être ouverte par le personnel avant de pouvoir commander.'
              : 'Saisissez le code temporaire à six chiffres communiqué par le personnel.'}
          </DialogDescription>

          {needsCode && (
            <form
              className="mt-5"
              onSubmit={async (event) => {
                event.preventDefault();
                if (sessionCode.length !== 6 || checkingCode) return;
                setCheckingCode(true);
                await submitTableSessionCode(sessionCode);
                setCheckingCode(false);
              }}
            >
              <label
                htmlFor="strict-table-session-code"
                className="text-xs font-bold text-charcoal-950 dark:text-white"
              >
                Code temporaire
              </label>
              <input
                ref={inputRef}
                id="strict-table-session-code"
                value={sessionCode}
                onChange={(event) =>
                  setSessionCode(event.target.value.replace(/\D/g, '').slice(0, 6))
                }
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                aria-invalid={errorCode === 'INVALID_SESSION_CODE'}
                className="mt-2 h-14 w-full min-w-0 rounded-2xl border border-slate-300 bg-white px-4 text-center font-mono text-xl font-black tracking-[0.3em] text-charcoal-950 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/25 dark:border-charcoal-700 dark:bg-charcoal-950 dark:text-white"
              />
              {errorCode === 'INVALID_SESSION_CODE' && (
                <p
                  role="alert"
                  className="mt-2 text-sm font-medium text-rose-700 dark:text-rose-300"
                >
                  Code incorrect. Vérifiez le code auprès du personnel.
                </p>
              )}
              <button
                type="submit"
                disabled={sessionCode.length !== 6 || checkingCode}
                className="mt-4 min-h-12 w-full rounded-2xl bg-charcoal-950 px-5 text-sm font-bold text-white transition-colors hover:bg-charcoal-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-45 dark:bg-white dark:text-charcoal-950"
              >
                {checkingCode ? 'Vérification…' : 'Valider et accéder au menu'}
              </button>
            </form>
          )}

          <p className="mt-4 text-center text-xs leading-5 text-slate-500">
            Ce contrôle est demandé une seule fois sur cet appareil.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function StoreTableBanner() {
  return (
    <div className="mx-auto my-3 max-w-5xl">
      <StoreTableContext variant="card" />
    </div>
  );
}
